<template>
  <div>
  <b-modal
    ref="modal"
    :title="isEdit ? 'แก้ไขส่วนลด' : 'เพิ่มส่วนลด'"
    size="lg"
    @ok="handleSave"
    ok-title="บันทึก"
    cancel-title="ยกเลิก"
  >
    <b-form>
      <h6 class="text-primary border-bottom pb-1 mb-3">
        <i class="fas fa-info-circle mr-1"></i> ข้อมูลทั่วไป
      </h6>
      <b-form-group label="ชื่อส่วนลด *" label-cols-sm="3">
        <b-form-input v-model="form.name" placeholder="เช่น ส่วนลดผู้สูงอายุ 10%" required />
      </b-form-group>
      <b-form-group label="คำอธิบาย" label-cols-sm="3">
        <b-form-textarea v-model="form.description" rows="2" placeholder="รายละเอียดเพิ่มเติม (ไม่บังคับ)" />
      </b-form-group>

      <h6 class="text-primary border-bottom pb-1 mb-3 mt-4">
        <i class="fas fa-calculator mr-1"></i> การคำนวณส่วนลด
      </h6>
      <b-form-group label="วิธีคิดส่วนลด" label-cols-sm="3">
        <b-form-radio-group v-model="form.discountMethod" :options="methodOptions" />
      </b-form-group>
      <b-form-group label="ประเภทส่วนลด" label-cols-sm="3">
        <b-form-radio-group v-model="form.discountType" :options="typeOptions" />
      </b-form-group>
      <!-- General discount -->
      <template v-if="form.discountMethod === 0">
        <b-form-group label="มูลค่าส่วนลด *" label-cols-sm="3">
          <b-input-group :append="form.discountType === 0 ? '%' : 'บาท'">
            <b-form-input v-model.number="form.discountValue" type="number" min="0" :max="form.discountType === 0 ? 100 : undefined" required />
          </b-input-group>
        </b-form-group>
      </template>
      <!-- Specific discount (DF/CF) -->
      <template v-if="form.discountMethod === 1">
        <b-alert show variant="info" class="py-2 px-3 small mb-3">
          <template v-if="form.discountType === 0">
            <strong>หมายเหตุ:</strong> เปอร์เซ็นต์คิดจาก<strong>ส่วนแบ่งของแต่ละฝ่าย</strong> ไม่ใช่จากราคารวม<br>
            เช่น ลดค่าหมอ 60% = ลด 60% ของส่วนแบ่งหมอ (DF)
          </template>
          <template v-else>
            <strong>หมายเหตุ:</strong> จำนวนเงินที่ระบุจะถูกจำกัดไว้ไม่เกินส่วนแบ่งของแต่ละฝ่าย
          </template>
        </b-alert>
        <b-form-group label="ส่วนลดหมอ (DF)" label-cols-sm="3" :description="form.discountType === 0 ? '% ของส่วนแบ่งหมอ' : 'จำกัดไม่เกินส่วนแบ่งหมอ'">
          <b-input-group :append="form.discountType === 0 ? '%' : 'บาท'">
            <b-form-input v-model.number="form.doctorDiscountValue" type="number" min="0" />
          </b-input-group>
        </b-form-group>
        <b-form-group label="ส่วนลดคลินิก (CF)" label-cols-sm="3" :description="form.discountType === 0 ? '% ของส่วนแบ่งคลินิก' : 'จำกัดไม่เกินส่วนแบ่งคลินิก'">
          <b-input-group :append="form.discountType === 0 ? '%' : 'บาท'">
            <b-form-input v-model.number="form.clinicDiscountValue" type="number" min="0" />
          </b-input-group>
        </b-form-group>
      </template>

      <!-- Preview panel: visible when exactly 1 treatId or itemId is selected and scope=ITEM -->
      <template v-if="previewCalc">
        <h6 class="text-primary border-bottom pb-1 mb-3 mt-4">
          <i class="fas fa-calculator mr-1"></i> ตัวอย่างการคำนวณ
        </h6>
        <b-card bg-variant="light" class="mb-3 small">
          <div class="mb-1 text-muted">
            <i class="fas fa-tag mr-1"></i>
            <strong>{{ previewTreatment.nameTh }}</strong>
          </div>
          <table class="table table-sm table-borderless mb-2" style="width:auto">
            <thead>
              <tr>
                <th style="width:90px"></th>
                <th class="text-right" style="width:110px">ก่อนลด</th>
                <th class="text-right" style="width:110px">หลังลด</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>ราคา</td>
                <td class="text-right">{{ previewCalc.originalPrice.toFixed(2) }} ฿</td>
                <td class="text-right text-success font-weight-bold">{{ previewCalc.finalPrice.toFixed(2) }} ฿</td>
              </tr>
              <tr>
                <td>DF</td>
                <td class="text-right">{{ previewCalc.dfAmount.toFixed(2) }} ฿</td>
                <td class="text-right text-success">{{ previewCalc.finalDf.toFixed(2) }} ฿</td>
              </tr>
              <tr>
                <td>CF</td>
                <td class="text-right">{{ previewCalc.cfAmount.toFixed(2) }} ฿</td>
                <td class="text-right text-success">{{ previewCalc.finalCf.toFixed(2) }} ฿</td>
              </tr>
              <tr class="border-top">
                <td>ส่วนลด</td>
                <td class="text-right">—</td>
                <td class="text-right text-danger">-{{ previewCalc.discountAmount.toFixed(2) }} ฿</td>
              </tr>
            </tbody>
          </table>
          <hr class="my-2" />
          <!-- Reverse calc: target price (method=0 only) -->
          <b-form-group v-if="form.discountMethod === 0" label="ต้องการราคา" label-cols-sm="4" class="mb-1" description="พิมพ์ราคาที่ต้องการ → ค่าส่วนลดจะถูกคำนวณอัตโนมัติ">
            <b-input-group append="฿">
              <b-form-input v-model.number="targetPrice" type="number" :min="0" :max="previewTreatment.price" step="0.01" placeholder="ราคาที่ต้องการ" size="sm" />
            </b-input-group>
          </b-form-group>
          <!-- Reverse calc: target DF (method=1 only) -->
          <b-form-group v-if="form.discountMethod === 1" label="ต้องการ DF" label-cols-sm="4" class="mb-1" description="พิมพ์ DF ที่ต้องการ → ส่วนลดหมอจะถูกคำนวณอัตโนมัติ">
            <b-input-group append="฿">
              <b-form-input v-model.number="targetDf" type="number" :min="0" :max="previewCalc.dfAmount" step="0.01" placeholder="DF ที่ต้องการ" size="sm" />
            </b-input-group>
          </b-form-group>
        </b-card>
      </template>

      <h6 class="text-primary border-bottom pb-1 mb-3 mt-4">
        <i class="fas fa-filter mr-1"></i> ขอบเขตการใช้งาน
      </h6>
      <b-form-group label="ขอบเขตส่วนลด" label-cols-sm="3">
        <b-form-radio-group v-model="form.scope" :options="scopeOptions" />
      </b-form-group>
      <b-form-group label="จำกัดรายการรักษา" label-cols-sm="3">
        <TreatmentPicker v-model="form.treatments" :clinic-url="clinicUrl" :branch-url="branchUrl" />
      </b-form-group>
      <b-form-group v-if="clinicType === 'CHAIN' || clinicType === 'CHAIN_NO_ERP'" label="จำกัดสาขา" label-cols-sm="3">
        <BranchPicker v-model="form.branchIds" />
      </b-form-group>

      <h6 class="text-primary border-bottom pb-1 mb-3 mt-4">
        <i class="fas fa-sliders-h mr-1"></i> เงื่อนไขการใช้งาน
      </h6>
      <b-form-group label="รหัสส่วนลด (Code)" label-cols-sm="3">
        <b-form-input v-model="form.code" placeholder="เช่น SONGKRAN2026" maxlength="50" />
        <small class="text-muted">ไม่กรอก = ไม่มีรหัส</small>
      </b-form-group>
      <b-form-group label="ช่วงเวลาที่ใช้ได้" label-cols-sm="3">
        <b-row>
          <b-col>
            <label class="small">วันเริ่มต้น</label>
            <b-form-datepicker v-model="form.startDt" :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }" placeholder="ไม่กำหนด" reset-button />
          </b-col>
          <b-col>
            <label class="small">วันสิ้นสุด</label>
            <b-form-datepicker v-model="form.endDt" :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }" placeholder="ไม่กำหนด" reset-button />
          </b-col>
        </b-row>
        <small class="text-muted">ไม่กำหนด = ใช้ได้ตลอด</small>
      </b-form-group>
      <b-form-group label="เฉพาะแพทย์" label-cols-sm="3">
        <DoctorPicker v-model="form.doctorIds" :clinic-url="clinicUrl" :branch-url="branchUrl" />
        <small class="text-muted">ไม่เลือก = แพทย์ทุกคนใช้ได้</small>
      </b-form-group>
      <b-form-group label="เฉพาะกลุ่มผู้ป่วย" label-cols-sm="3">
        <PatientGroupPicker v-model="form.patientGroupIds" :clinic-url="clinicUrl" :branch-url="branchUrl" />
        <small class="text-muted">ไม่เลือก = ผู้ป่วยทุกกลุ่มใช้ได้</small>
      </b-form-group>
      <b-form-group label="จำนวนครั้งที่ใช้ได้" label-cols-sm="3">
        <b-form-input type="number" v-model.number="form.maxUsage" min="1" placeholder="ไม่จำกัด" />
        <small class="text-muted">ไม่กรอก = ใช้ได้ไม่จำกัดครั้ง</small>
      </b-form-group>
      <b-form-group label="เพดานส่วนลดสูงสุด (%)" label-cols-sm="3">
        <b-form-input type="number" v-model.number="form.maxDiscountPercent" min="0" max="100" step="0.01" placeholder="ไม่จำกัด" />
        <small class="text-muted">ไม่กรอก = ไม่มีเพดาน</small>
      </b-form-group>
      <b-form-group label="สิทธิ์การใช้ส่วนลด" label-cols-sm="3">
        <b-form-checkbox-group v-model="form.allowedRoles" :options="roleOptions" />
        <small class="text-muted">ไม่เลือก = พนักงานทุกตำแหน่งใช้ได้</small>
      </b-form-group>

      <h6 class="text-primary border-bottom pb-1 mb-3 mt-4">
        <i class="fas fa-toggle-on mr-1"></i> สถานะ
      </h6>
      <b-form-group label="สถานะ" label-cols-sm="3">
        <b-form-checkbox v-model="form.isActive" switch :value="1" :unchecked-value="0">
          {{ form.isActive ? "เปิดใช้งาน" : "ปิดใช้งาน" }}
        </b-form-checkbox>
      </b-form-group>
    </b-form>
  </b-modal>
  <Dialog ref="Dialog" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import TreatmentPicker from "./TreatmentPicker.vue";
import BranchPicker from "./BranchPicker.vue";
import DoctorPicker from "./DoctorPicker.vue";
import PatientGroupPicker from "./PatientGroupPicker.vue";
import Dialog from "@/components/modal/Dialog.vue";

export default {
  name: "DiscountForm",
  components: { TreatmentPicker, BranchPicker, DoctorPicker, PatientGroupPicker, Dialog },
  props: {
    clinicType: { type: String, default: "STANDALONE" },
  },
  computed: {
    ...mapGetters({
      getTreatList: "moduleAppointment/getTreatList",
      getItemList: "moduleAppointment/getItemList",
      isFileStorageExpired: "moduleBranch/isFileStorageExpired",
      getDiscountList: "moduleDiscount/getDiscountList",
    }),
    canCreateNewDiscount() {
      // Phase 0 subscription gating: unsubscribed users capped at FREE_DISCOUNT_CAP.
      if (!this.isFileStorageExpired) return true;
      return (this.getDiscountList || []).length < 2;
    },
    previewTreatment() {
      if (this.form.scope !== "ITEM" || this.form.treatments.length !== 1) return null;
      const t = this.form.treatments[0];
      if (t.treatId) return (this.getTreatList || []).find(tr => tr.id === t.treatId) || null;
      if (t.itemId) return (this.getItemList || []).find(it => it.id === t.itemId) || null;
      return null;
    },
    previewCalc() {
      const t = this.previewTreatment;
      if (!t) return null;
      const price = parseFloat(t.price) || 0;
      const dfAmount = t.dfType === 1 ? parseFloat(t.df) || 0 : price * (parseFloat(t.df) || 0) / 100;
      const cfAmount = price - dfAmount;
      const { discountMethod, discountType, discountValue, doctorDiscountValue, clinicDiscountValue } = this.form;
      let finalDf, finalCf, finalPrice;
      if (discountMethod === 1) {
        if (discountType === 0) {
          finalDf = Math.max(0, dfAmount * (1 - (doctorDiscountValue || 0) / 100));
          finalCf = Math.max(0, cfAmount * (1 - (clinicDiscountValue || 0) / 100));
        } else {
          finalDf = Math.max(0, dfAmount - (doctorDiscountValue || 0));
          finalCf = Math.max(0, cfAmount - (clinicDiscountValue || 0));
        }
        finalPrice = finalDf + finalCf;
      } else {
        if (discountType === 0) {
          finalPrice = Math.max(0, price * (1 - (discountValue || 0) / 100));
        } else {
          finalPrice = Math.max(0, price - (discountValue || 0));
        }
        const ratio = price > 0 ? finalPrice / price : 0;
        finalDf = dfAmount * ratio;
        finalCf = cfAmount * ratio;
      }
      return {
        originalPrice: price,
        dfAmount,
        cfAmount,
        finalPrice: Math.round(finalPrice * 100) / 100,
        finalDf: Math.round(finalDf * 100) / 100,
        finalCf: Math.round(finalCf * 100) / 100,
        discountAmount: Math.round((price - finalPrice) * 100) / 100,
      };
    },
  },
  watch: {
    targetPrice(val) {
      if (!this.previewTreatment || val === null || val === "" || this.form.discountMethod !== 0) return;
      const price = parseFloat(this.previewTreatment.price) || 0;
      if (price <= 0) return;
      if (this.form.discountType === 0) {
        this.form.discountValue = Math.max(0, Math.min(100, Math.round((1 - val / price) * 10000) / 100));
      } else {
        this.form.discountValue = Math.max(0, Math.min(price, Math.round((price - val) * 100) / 100));
      }
    },
    targetDf(val) {
      if (!this.previewTreatment || val === null || val === "" || this.form.discountMethod !== 1) return;
      const { price, df, dfType } = this.previewTreatment;
      const dfAmount = dfType === 1 ? parseFloat(df) || 0 : (parseFloat(price) || 0) * (parseFloat(df) || 0) / 100;
      if (dfAmount <= 0) return;
      if (this.form.discountType === 0) {
        this.form.doctorDiscountValue = Math.max(0, Math.min(100, Math.round((1 - val / dfAmount) * 10000) / 100));
      } else {
        this.form.doctorDiscountValue = Math.max(0, Math.min(dfAmount, Math.round((dfAmount - val) * 100) / 100));
      }
    },
    "form.treatments"() { this.targetPrice = null; this.targetDf = null; },
    "form.discountMethod"() { this.targetPrice = null; this.targetDf = null; },
    "form.discountType"() { this.targetPrice = null; this.targetDf = null; },
  },
  data() {
    return {
      isEdit: false,
      editId: null,
      clinicUrl: "",
      branchUrl: "",
      targetPrice: null,
      targetDf: null,
      form: this.defaultForm(),
      methodOptions: [
        { text: "ทั่วไป (ลดรวม)", value: 0 },
        { text: "กำหนดเอง (DF/CF)", value: 1 },
      ],
      typeOptions: [
        { text: "เปอร์เซ็นต์ (%)", value: 0 },
        { text: "จำนวนเงิน (บาท)", value: 1 },
      ],
      scopeOptions: [
        { text: "รายการ (ITEM)", value: "ITEM" },
        { text: "ใบเสร็จ (RECEIPT)", value: "RECEIPT" },
      ],
      roleOptions: [
        { text: "ผู้จัดการ", value: 1 },
        { text: "หัวหน้าเคาน์เตอร์", value: 5 },
        { text: "เคาน์เตอร์", value: 3 },
      ],
    };
  },
  methods: {
    ...mapActions("moduleDiscount", ["createDiscount", "updateDiscount"]),
    defaultForm() {
      return {
        name: "",
        description: "",
        discountMethod: 0,
        discountType: 0,
        discountValue: 0,
        doctorDiscountValue: 0,
        clinicDiscountValue: 0,
        scope: "ITEM",
        branchIds: null,
        treatments: [],
        isActive: 1,
        code: null,
        startDt: null,
        endDt: null,
        doctorIds: null,
        patientGroupIds: null,
        maxUsage: null,
        maxDiscountPercent: null,
        allowedRoles: [],
      };
    },
    show({ clinicUrl, branchUrl, discount }) {
      this.clinicUrl = clinicUrl;
      this.branchUrl = branchUrl;

      if (discount) {
        this.isEdit = true;
        this.editId = discount.id;
        this.form = {
          name: discount.name,
          description: discount.description || "",
          discountMethod: discount.discountMethod,
          discountType: discount.discountType,
          discountValue: parseFloat(discount.discountValue),
          doctorDiscountValue: discount.doctorDiscountValue ? parseFloat(discount.doctorDiscountValue) : 0,
          clinicDiscountValue: discount.clinicDiscountValue ? parseFloat(discount.clinicDiscountValue) : 0,
          scope: discount.scope || "ITEM",
          branchIds: discount.branchIds || null,
          treatments: (discount.treatments || []).map(t => ({
            treatId: t.treatId,
            treatCateId: t.treatCateId,
            itemId: t.itemId,
          })),
          isActive: discount.isActive,
          code: discount.code || null,
          startDt: discount.startDt ? discount.startDt.slice(0, 10) : null,
          endDt: discount.endDt ? discount.endDt.slice(0, 10) : null,
          doctorIds: discount.doctorIds || null,
          patientGroupIds: discount.patientGroupIds || null,
          maxUsage: discount.maxUsage || null,
          maxDiscountPercent: discount.maxDiscountPercent ? parseFloat(discount.maxDiscountPercent) : null,
          allowedRoles: discount.allowedRoles || [],
        };
      } else {
        this.isEdit = false;
        this.editId = null;
        this.form = this.defaultForm();
      }

      this.$refs.modal.show();
    },
    async handleSave(bvModalEvent) {
      bvModalEvent.preventDefault();

      // Defense-in-depth: matches the disabled "Add discount" button in
      // DiscountManagement.vue. Subscribed users unlimited; unsubscribed
      // capped at 2 total. Editing existing discounts is always allowed.
      if (!this.isEdit && !this.canCreateNewDiscount) {
        this.$refs.Dialog.showToast(
          "warning",
          "แพ็คเกจฟรีสร้างได้สูงสุด 2 ส่วนลด — สมัคร subscription เพื่อสร้างเพิ่ม"
        );
        return;
      }

      if (!this.form.name.trim()) {
        this.$refs.Dialog.showToast("danger", "กรุณาระบุชื่อส่วนลด");
        return;
      }

      if (this.form.startDt && this.form.endDt && this.form.endDt < this.form.startDt) {
        this.$refs.Dialog.showToast("danger", "วันสิ้นสุดต้องไม่อยู่ก่อนวันเริ่มต้น");
        return;
      }

      if (this.form.maxDiscountPercent !== null && this.form.maxDiscountPercent !== "" && this.form.maxDiscountPercent > 100) {
        this.$refs.Dialog.showToast("danger", "เพดานส่วนลดสูงสุดต้องไม่เกิน 100%");
        return;
      }

      const data = {
        ...this.form,
        startDt: this.form.startDt || null,
        endDt: this.form.endDt || null,
        allowedRoles: this.form.allowedRoles?.length ? this.form.allowedRoles : null,
      };

      try {
        if (this.isEdit) {
          await this.updateDiscount({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
            id: this.editId,
            data,
          });
        } else {
          await this.createDiscount({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
            data,
          });
        }

        this.$refs.Dialog.showToast("success", this.isEdit ? "แก้ไขส่วนลดสำเร็จ" : "สร้างส่วนลดสำเร็จ");
        this.$refs.modal.hide();
        this.$emit("saved");
      } catch {
        // ErrorService handles error toast automatically via axios interceptor
      }
    },
  },
};
</script>

<style scoped>
.modal-body .form-group {
  margin-bottom: 0.75rem;
}
</style>
